import { Button, Stack, Text, Alert } from '@mantine/core';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { useState } from 'react';
import { GraphQLQuery } from '@aws-amplify/api';
import { paypalClientId } from '../config/paypal';
import { billingClient } from '../../billing-api-integration/client';
import { notifyError } from '../../../../components/ServiceNotifications';
import { createPaymentProfile } from '../../payment-profiles/services/PaymentProfilesService';
import { fetchEnabledBillingProviders } from '../../billing-providers/services/BillingProvidersService';
import { BillingProvider } from '../../billing-providers/types';

interface CreateOrderResponse {
    createOrder: {
        id: string;
        customerId: string;
        status: string;
        providerKey: string;
        providerOrderId: string;
        amount: number;
        currency: string;
    };
}

const createOrderMutation = `
    mutation createOrder($input: CreateOrderInput!) {
        createOrder(input: $input) {
            id
            customerId
            status
            providerKey
            providerOrderId
            amount
            currency
        }
    }
`;

interface PayPalPaymentMethodSetupFormProps {
    customerId: string;
    onSuccess: () => void;
    onCancel: () => void;
}

export const PayPalPaymentMethodSetupForm = ({
    customerId,
    onSuccess,
    onCancel
}: PayPalPaymentMethodSetupFormProps) => {
    const [loading, setLoading] = useState(false);

    if (!paypalClientId) {
        return (
            <Stack gap="md">
                <Alert color="red" title="Configuration Error">
                    PayPal Client ID is not configured. Please add your PayPal Client ID to the configuration.
                </Alert>
                <Button variant="subtle" onClick={onCancel}>
                    Cancel
                </Button>
            </Stack>
        );
    }

    return (
        <Stack gap="md">
            <Text size="sm" c="dimmed">
                Use PayPal sandbox test account to authorize payment
            </Text>
            <Text size="xs" c="blue">
                Test account: sb-buyer@personal.example.com / password
            </Text>
            <PayPalButtons
                style={{ layout: 'vertical' }}
                createOrder={async () => {
                    setLoading(true);
                    try {
                        const response = await billingClient.graphql<GraphQLQuery<CreateOrderResponse>>({
                            query: createOrderMutation,
                            variables: {
                                input: {
                                    customerId,
                                    providerKey: 'paypal',
                                    amount: 0,
                                    currency: 'USD',
                                    description: 'Payment method setup',
                                }
                            }
                        });

                        const order = response.data?.createOrder;
                        if (!order?.providerOrderId) {
                            throw new Error('Failed to create PayPal order');
                        }

                        return order.providerOrderId;
                    } catch (error) {
                        console.error('Failed to create order:', error);
                        setLoading(false);
                        throw error;
                    }
                }}
                onApprove={async (data) => {
                    setLoading(true);
                    try {
                        const providers = await fetchEnabledBillingProviders();
                        const paypalProvider = providers.find((p: BillingProvider) => p.key === 'paypal');

                        if (paypalProvider) {
                            await createPaymentProfile({
                                name: 'PayPal Payment Method',
                                status: 'Active',
                                customerId: customerId,
                                billingProviderId: paypalProvider.id,
                                providerPaymentMethodId: data.orderID
                            });
                        }

                        onSuccess();
                    } catch (error) {
                        console.error('Failed to save payment profile:', error);
                        notifyError(error);
                        setLoading(false);
                    }
                }}
                onCancel={() => {
                    setLoading(false);
                }}
                onError={(err) => {
                    console.error('PayPal error:', err);
                    setLoading(false);
                }}
            />
            <Button variant="subtle" onClick={onCancel} disabled={loading}>
                Cancel
            </Button>
        </Stack>
    );
};
